import lendScoreRoiData from './lendScoreRoiData.json';

type RoiData = typeof lendScoreRoiData;

export type LoanType = keyof RoiData;
// Every score model there is data for: LendScore, which every loan type has,
// and the model that belongs to a single loan type.
export type ScoreModel = { [Loan in LoanType]: keyof RoiData[Loan] }[LoanType];
export type Segment = keyof RoiData['overall']['lendScore'];

// The bands in the ROI data, as stored: fractions, one per 5% of applicants.
export type RateSeries = { industry: number[]; combo: number[] };

export type Projection = {
  // Percentages, to match the sliders and the card.
  approvalRate: { without: number; with: number; increase: number };
  lossRate: {
    without: number;
    with: number;
    // A fraction: 0.34 means 34% fewer losses.
    reduction: number;
  };
};

// Each band is one 5% slice of applicants, ranked from best to worst by the
// score, so the approval rate only has a loss rate of its own at multiples of
// 5. Approving the best 60% means approving the first 12 bands, and the loss
// rate is the average of their rates.
const BAND_SIZE = 5;

// The bands are rounded to 0.01 of a percentage point, so two loss rates that
// differ by less than that are the same rate. Without this, approving everyone
// would leave the combo a hair above the industry score's loss rate (the sheet
// has them equal) and report an approval rate just under 100.
const ROUNDING = 0.01;

const BAND_COUNT = 100 / BAND_SIZE;

// A band is a fraction of the applicants in it that were lost, so anything
// else is a gap in the data rather than a rate: a band that is missing, empty,
// not a number, or a loss rate that is negative or over 100%.
const hasBands = (bands: unknown): bands is number[] => {
  return (
    Array.isArray(bands) &&
    bands.length === BAND_COUNT &&
    bands.every((rate) => {
      return typeof rate === 'number' && rate >= 0 && rate <= 1;
    })
  );
};

// Both scores are needed for anything to be worked out: the industry score is
// what LendScore is measured against, and the combo is LendScore itself. A
// series short of either has nothing to say.
export const hasRateData = (
  series: Partial<Record<keyof RateSeries, unknown>> | undefined,
): series is RateSeries => {
  return hasBands(series?.industry) && hasBands(series?.combo);
};

// Not every model is offered for every loan type, and the types can't say
// which, so a combination with no data is left out rather than guessed at with
// some other series.
export const findRateSeries = (
  loanType: LoanType,
  model: ScoreModel,
  segment: Segment,
): RateSeries | undefined => {
  const models: Record<string, Record<string, RateSeries> | undefined> =
    lendScoreRoiData[loanType];
  const series = models[model]?.[segment];

  return hasRateData(series) ? series : undefined;
};

export const getRateSeries = (
  loanType: LoanType,
  model: ScoreModel,
  segment: Segment,
): RateSeries => {
  const series = findRateSeries(loanType, model, segment);

  if (!series) {
    throw new Error(
      `There is no LendScore ROI data for ${loanType} loans with the ${model} model and the ${segment} segment`,
    );
  }

  return series;
};

// The loss rate, as a percentage, after approving the first `k` bands, for
// each `k` from 0 to the last band. Approving nobody has no losses.
const cumulativeLossRates = (bands: number[]) => {
  const rates = [0];
  let total = 0;

  bands.forEach((rate, index) => {
    total += rate;
    rates.push((total / (index + 1)) * 100);
  });

  return rates;
};

const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

// Between bands the loss rate is taken to rise in a straight line, so a rate
// that isn't a multiple of 5 still lands on the curve.
const lossRateAt = (curve: number[], approvalRate: number) => {
  const position = clamp(approvalRate, 0, 100) / BAND_SIZE;
  const below = Math.min(Math.floor(position), curve.length - 2);
  const fraction = position - below;

  return curve[below] + (curve[below + 1] - curve[below]) * fraction;
};

// The highest approval rate that keeps losses within `lossRate`. The curve
// isn't guaranteed to rise every step (a band of unusually safe applicants
// pulls the average back down), so this looks for the highest rate that fits
// rather than the first one that doesn't.
const approvalRateAt = (curve: number[], lossRate: number) => {
  const last = curve.length - 1;

  for (let band = last; band >= 0; band -= 1) {
    if (curve[band] <= lossRate + ROUNDING) {
      if (band === last) return 100;

      const fraction = Math.max(
        0,
        (lossRate - curve[band]) / (curve[band + 1] - curve[band]),
      );

      return (band + fraction) * BAND_SIZE;
    }
  }

  return 0;
};

/**
 * The approval rate to land on when the delinquency slider is moved to
 * `lossRate`. Only the approval rate's own steps have a delinquency rate, so
 * this is the step whose rate is closest, and a rate that falls exactly
 * between two steps goes to the higher approval rate. Whatever `lossRate` is,
 * the answer is a step of this series, so the slider can never leave the range
 * the series covers: from 0% up to the highest rate it reaches.
 */
export const approvalRateNearLossRate = (
  series: RateSeries,
  lossRate: number,
): number => {
  const steps = cumulativeLossRates(series.industry);
  let nearest = 0;

  steps.forEach((rate, step) => {
    if (Math.abs(rate - lossRate) <= Math.abs(steps[nearest] - lossRate)) {
      nearest = step;
    }
  });

  return nearest * BAND_SIZE;
};

/**
 * The approval rate one step along the delinquency slider from
 * `approvalRate`: `1` for the step with the next higher delinquency rate and
 * `-1` for the next lower. Ordered by delinquency rate rather than by approval
 * rate, because a curve that falls for a step doesn't visit its steps in
 * order. At either end of the range there is nowhere further to go, so it
 * stays where it is.
 */
export const adjacentApprovalRate = (
  series: RateSeries,
  approvalRate: number,
  direction: 1 | -1,
): number => {
  const steps = cumulativeLossRates(series.industry);
  const current =
    steps[clamp(Math.round(approvalRate / BAND_SIZE), 0, steps.length - 1)];
  let adjacent = -1;

  for (let step = 0; step < steps.length; step += 1) {
    const distance = (steps[step] - current) * direction;

    if (
      distance > 0 &&
      (adjacent === -1 || distance < (steps[adjacent] - current) * direction)
    ) {
      adjacent = step;
    }
  }

  return adjacent === -1 ? approvalRate : adjacent * BAND_SIZE;
};

/**
 * What LendScore changes for a lender approving `approvalRate` percent of
 * applicants today. Losses stay where they are and the approval rate grows,
 * or the approval rate stays where it is and losses shrink.
 */
export const projectFromApprovalRate = (
  series: RateSeries,
  approvalRate: number,
): Projection => {
  const industry = cumulativeLossRates(series.industry);
  const combo = cumulativeLossRates(series.combo);

  const approvalWithout = clamp(approvalRate, 0, 100);
  const lossWithout = lossRateAt(industry, approvalWithout);
  const lossWith = lossRateAt(combo, approvalWithout);
  const approvalWith = approvalRateAt(combo, lossWithout);

  return {
    approvalRate: {
      without: approvalWithout,
      with: approvalWith,
      increase: approvalWith - approvalWithout,
    },
    lossRate: {
      without: lossWithout,
      with: lossWith,
      reduction: lossWithout === 0 ? 0 : 1 - lossWith / lossWithout,
    },
  };
};
